import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { BarChart3, Trophy, FileText, User, Zap, Menu, Clock, UserCheck, LogOut, Key, LogIn, Lightbulb, Mail, MailX, Crown, Users, ChevronDown, Info, ShoppingBag, Edit } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import horsebeatLogo from "@/assets/horsebeat-logo.png";
import { useEmailSubscription } from "@/hooks/useEmailSubscription";
import { useUserProfile } from "@/hooks/useUserProfile";
import { SyndicateNotifications } from "@/components/SyndicateNotifications";
import { RacesMegaMenu } from "@/components/RacesMegaMenu";
import { ShoppingCart } from "@/components/ShoppingCart";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const HeaderNavigation = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isAuthenticated, user, signOut } = useAuth();
  const { isSubscribed, loading: subscriptionLoading, subscribe, unsubscribe } = useEmailSubscription();
  const [isOpen, setIsOpen] = useState(false);
const { profile, updateFantasyTestingOptIn, loading: profileLoading } = useUserProfile();
const [fantasyTestOptIn, setFantasyTestOptIn] = useState(false);
useEffect(() => {
  if (profile?.fantasy_testing_opt_in !== undefined && profile?.fantasy_testing_opt_in !== null) {
    setFantasyTestOptIn(!!profile.fantasy_testing_opt_in);
  }
}, [profile?.fantasy_testing_opt_in]);
const handleFantasyToggle = async (val: boolean) => {
  setFantasyTestOptIn(val);
  try {
    await updateFantasyTestingOptIn(val);
  } catch {
    setFantasyTestOptIn(!val);
  }
};

  const mainMenuItems = [
    {
      title: "Spolumajitelství",
      path: "/syndicates",
      icon: Users,
    },
    {
      title: "Shop",
      path: "/shop",
      icon: ShoppingBag,
    },
    {
      title: "Koně",
      path: "/horses",
      icon: Zap,
    },
    {
      title: "Jezdci",
      path: "/jockeys", 
      icon: User,
    },
    {
      title: "Dostihy",
      icon: Trophy,
      submenu: [
        {
          title: "Výsledky",
          path: "/race-results",
          icon: Trophy,
          description: "Výsledky dostihů"
        },
        {
          title: "Časy/distance",
          path: "/time-distance",
          icon: Clock,
          description: "Analýza časů a distancí"
        },
        {
          title: "Kurzy",
          path: "/odds-analysis",
          icon: BarChart3,
          description: "Sázkové kurzy"
        },
        {
          title: "Tipy",
          path: "/tip-analysis",
          icon: FileText,
          description: "Tipování dostihů"
        },
        {
          title: "Fantasy",
          path: "/fantasy",
          icon: Crown,
          description: "Fantasy liga"
        }
      ]
    },
    {
      title: "Články",
      path: "/articles",
      icon: FileText,
    },
    {
      title: "O nás",
      path: "/o-nas",
      icon: Info,
    }
  ];

  // Flatten menu items for mobile
  const flatMenuItems = mainMenuItems.flatMap(item => {
    if (item.submenu) {
      return item.submenu;
    }
    return [{
      title: item.title,
      path: item.path!,
      icon: item.icon,
      description: ""
    }];
  });

const externalLinks: Array<{ title: string; path: string; icon: any }> = [];

  if (isMobile) {
    return (
      <div className="bg-[hsl(var(--header-dark))] backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div 
              className="flex items-center gap-3 cursor-pointer shrink-0" 
              onClick={() => navigate("/")}
            >
              <img src={horsebeatLogo} alt="Horsebeat Logo" className="h-12 w-auto" />
            </div>
            
            {/* Help Guide and Notifications */}
            <div className="flex items-center gap-1">
              <LanguageSwitcher />
              {isAuthenticated && <SyndicateNotifications />}
            </div>
            
            {/* User Status and Mobile Menu */}
            <div className="flex items-center gap-2">
              {isAuthenticated ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="hover:bg-white/20 text-white hover:text-white">
                      <UserCheck className="h-5 w-5 text-green-400" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-4 bg-[hsl(var(--header-dark))] border-white/10" align="end">
                  <div className="space-y-3">
                     <div className="border-b pb-2 border-white/10">
                      {profile?.username && (
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="font-semibold text-sm text-white truncate">{profile.username}</p>
                          <button
                            onClick={() => navigate("/auth?edit-profile=true")}
                            className="text-xs text-complement-1 hover:underline shrink-0"
                            title="Upravit profil"
                          >
                            Upravit
                          </button>
                        </div>
                      )}
                      <p className="font-medium text-sm text-white truncate">{user?.email}</p>
                      <p className="text-xs text-white/60">Přihlášený uživatel</p>
                      {isSubscribed !== null && (
                        <p className="text-xs text-white/60 mt-1">
                          Emaily: {isSubscribed ? "✅ Aktivní" : "❌ Neaktivní"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      {/* User Dashboard Link */}
                       <Button 
                        onClick={() => navigate("/user-dashboard")}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Můj účet/moji koně
                      </Button>
                          {/* Email Subscription Toggle */}
                          <Button 
                            onClick={isSubscribed ? unsubscribe : subscribe}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                            disabled={subscriptionLoading}
                          >
                            {isSubscribed ? (
                              <>
                                <MailX className="h-4 w-4 mr-2" />
                                Odhlásit z emailů
                              </>
                            ) : (
                              <>
                                <Mail className="h-4 w-4 mr-2" />
                                Přihlásit k emailům
                              </>
                            )}
                          </Button>
                          <Button 
                            onClick={() => navigate("/auth?edit-profile=true")}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Upravit profil
                          </Button>
                          <Button 
                            onClick={() => navigate("/auth?change-password=true")}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                          >
                            <Key className="h-4 w-4 mr-2" />
                            Změnit heslo
                          </Button>
                          <Button 
                            onClick={signOut}
                            variant="outline"
                            size="sm"
                            className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                          >
                            <LogOut className="h-4 w-4 mr-2" />
                            Odhlásit se
                          </Button>
                        </div>
                    </div>
                  </PopoverContent>
                </Popover>
              ) : (
                <Button 
                  variant="ghost"
                  size="icon"
                  className="hover:bg-white/20 text-white hover:text-white border-[3px] border-blue-400 hover:border-blue-300"
                  onClick={() => navigate("/auth")}
                >
                  <LogIn className="h-5 w-5 text-blue-400" />
                </Button>
              )}
               <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 hover:text-white">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <ScrollArea className="h-full pr-4">
                  <nav className="flex flex-col gap-2 py-6">
                    {flatMenuItems.map((item) => (
                      <Button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setIsOpen(false);
                        }}
                        variant="ghost"
                        className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {item.title}
                          {item.path === "/fantasy" && (
                            <sup className="text-xs text-muted-foreground ml-0.5">Beta</sup>
                          )}
                        </span>
                      </Button>
                    ))}
                    
                    <Button
                      onClick={() => {
                        navigate("/help");
                        setIsOpen(false);
                      }}
                      variant="ghost"
                      className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                    >
                      <Lightbulb className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm font-medium">Nápověda a průvodce</span>
                    </Button>

                    {/* External Links */}
                    {externalLinks.map((item) => (
                      <Button
                        key={item.path}
                        onClick={() => {
                          window.open(item.path, '_blank');
                          setIsOpen(false);
                        }}
                        variant="ghost"
                        className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </Button>
                    ))}
                  </nav>
                </ScrollArea>
              </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[hsl(var(--header-dark))] backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex min-w-0 items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer shrink-0" 
            onClick={() => navigate("/")}
          >
            <img src={horsebeatLogo} alt="Horsebeat Logo" className="h-14 w-auto" />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden xl:flex items-center gap-2 xl:ml-auto">
            {mainMenuItems.map((item) => (
              item.submenu ? (
                <Popover key={item.title}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-auto py-2 px-3 text-white hover:bg-white/20 hover:text-white"
                    >
                      <item.icon className="h-5 w-5 mr-2" />
                      <span className="text-base font-medium">{item.title}</span>
                      <ChevronDown className="h-4 w-4 ml-1" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-0 shadow-lg" align="start" sideOffset={8}>
                    <RacesMegaMenu />
                  </PopoverContent>
                </Popover>
              ) : (
                <Button
                  key={item.title}
                  onClick={() => navigate(item.path!)}
                  variant="ghost"
                  className="h-auto py-2 px-3 text-white hover:bg-white/20 hover:text-white"
                >
                  <item.icon className="h-5 w-5 mr-2" />
                  <span className="text-base font-medium">
                    {item.title}
                    {item.path === "/fantasy" && (
                      <sup className="text-xs text-white/70 ml-0.5">Beta</sup>
                    )}
                  </span>
                </Button>
              )
            ))}
            
            {/* External Links */}
            {externalLinks.map((item) => (
              <Button
                key={item.path}
                onClick={() => window.open(item.path, '_blank')}
                variant="ghost"
                className="h-auto py-2 px-3 text-white hover:bg-white/20 hover:text-white"
              >
                <item.icon className="h-5 w-5 mr-2" />
                <span className="text-base font-medium">{item.title}</span>
              </Button>
            ))}
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            <LanguageSwitcher />
            <Button 
              variant="ghost" 
              size="icon" 
              className="hover:bg-white/20 text-white hover:text-white"
              onClick={() => navigate("/help")}
              title="Nápověda a průvodce"
            >
              <Lightbulb className="h-5 w-5 text-yellow-400" />
            </Button>
            {isAuthenticated && <SyndicateNotifications />}
            {isAuthenticated ? (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-white/20 text-white hover:text-white">
                    <UserCheck className="h-5 w-5 text-green-400" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-4 bg-[hsl(var(--header-dark))] border-white/10" align="end">
                  <div className="space-y-3">
                    <div className="border-b pb-2 border-white/10">
                      {profile?.username && (
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className="font-semibold text-sm text-white truncate">{profile.username}</p>
                          <button
                            onClick={() => navigate("/auth?edit-profile=true")}
                            className="text-xs text-complement-1 hover:underline shrink-0"
                            title="Upravit profil"
                          >
                            Upravit
                          </button>
                        </div>
                      )}
                      <p className="font-medium text-sm text-white truncate">{user?.email}</p>
                      <p className="text-xs text-white/60">Přihlášený uživatel</p>
                      {isSubscribed !== null && (
                        <p className="text-xs text-white/60 mt-1">
                          Emaily: {isSubscribed ? "✅ Aktivní" : "❌ Neaktivní"}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      {/* User Dashboard Link */}
                      <Button 
                        onClick={() => navigate("/user-dashboard")}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Můj účet/moji koně
                      </Button>
                      <Button 
                        onClick={isSubscribed ? unsubscribe : subscribe}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                        disabled={subscriptionLoading}
                      >
                        {isSubscribed ? (
                          <>
                            <MailX className="h-4 w-4 mr-2" />
                            Odhlásit z emailů
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-2" />
                            Přihlásit k emailům
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={() => navigate("/auth?edit-profile=true")}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Upravit profil
                      </Button>
                      <Button 
                        onClick={() => navigate("/auth?change-password=true")}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        <Key className="h-4 w-4 mr-2" />
                        Změnit heslo
                      </Button>
                      <Button 
                        onClick={signOut}
                        variant="outline"
                        size="sm"
                        className="w-full justify-start bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Odhlásit se
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            ) : (
              <Button 
                variant="ghost"
                size="icon"
                className="hover:bg-white/20 text-white hover:text-white border-[3px] border-blue-400 hover:border-blue-300"
                onClick={() => navigate("/auth")}
              >
                <LogIn className="h-5 w-5 text-blue-400" />
              </Button>
            )}
          </div>
          {/* Mobile Menu Button */}
          <div className="xl:hidden">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 hover:text-white">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <ScrollArea className="h-full pr-4">
                  <nav className="flex flex-col gap-2 py-6">
                     {flatMenuItems.map((item) => (
                       <Button
                         key={item.path}
                         onClick={() => {
                           navigate(item.path);
                           setIsOpen(false);
                         }}
                         variant="ghost"
                         className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                       >
                         <item.icon className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            {item.title}
                            {item.path === "/fantasy" && (
                              <sup className="text-xs text-muted-foreground ml-0.5">Beta</sup>
                            )}
                          </span>
                       </Button>
                     ))}
                    
                     <Button
                       onClick={() => {
                         navigate("/help");
                         setIsOpen(false);
                       }}
                       variant="ghost"
                       className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                     >
                       <Lightbulb className="h-4 w-4 text-yellow-400" />
                       <span className="text-sm font-medium">Nápověda a průvodce</span>
                     </Button>

                     {/* External Links */}
                    {externalLinks.map((item) => (
                      <Button
                        key={item.path}
                        onClick={() => {
                          window.open(item.path, '_blank');
                          setIsOpen(false);
                        }}
                        variant="ghost"
                        className="flex items-center gap-2 px-3 py-3 h-auto justify-start"
                      >
                        <item.icon className="h-4 w-4" />
                        <span className="text-sm font-medium">{item.title}</span>
                      </Button>
                    ))}
                  </nav>
                </ScrollArea>
               </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </div>
  );
};
